import {useAppContext} from "../../context/AppContext";
import {ScrollableList} from "../../../common/utils/Util";
import {IDClass} from "../../model/CommonTypes";
import {useRef} from "react";

type TableScroll = {
    x: number
    y: number
}

export interface TableCache {
    data?: ScrollableList<IDClass & any>
    tableScroll?: TableScroll
}

type InternalTableCache = {
    group?: string
    data: {[typ:string]: TableCache }
}

type LocalStorageTableCache = {
    [key: string]: TableScroll;
}

export const useTableCache = (typ: string, group?: string) => {
    const {navigationHistory} = useAppContext();
    const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);
    const CACHE_NAME = 'table_cache';
    const { getCache, setCache } = useAppContext();
    const getParsedCache = () => {
        return (JSON.parse(getCache(CACHE_NAME) ?? null) ?? {typ: typ, data: {[typ] : {}}}) as InternalTableCache;
    }
    const getTableCache = () : TableCache => {
        let cache = getParsedCache();
        return (cache.group === group ? getParsedCache().data?.[typ] ?? {} : {}) as TableCache;
    };
    const setTableCache = (data: TableCache) => {
        let cache = getParsedCache();
        let currentData = cache.group != null && cache.group === group ? cache.data : {};
        setCache(CACHE_NAME, JSON.stringify({
            group: group,
            data: {...currentData, [typ]: data}
        }));

        // Debounced localStorage update
        if (data?.tableScroll) {
            if (debounceTimerRef.current) {
                clearTimeout(debounceTimerRef.current);
            }

            debounceTimerRef.current = setTimeout(() => {
                let existingScroll = JSON.parse(localStorage.getItem("tableScroll") ?? "null") as LocalStorageTableCache;

                const updatedScroll = {
                    ...(existingScroll || {}),
                    [typ]: data.tableScroll
                };

                localStorage.setItem("tableScroll", JSON.stringify(updatedScroll));
            }, 200);
        }
    }

    const clearTableCache = () => {
        setTableCache(null);
    }

    /**
     * Funkcia ktora na zaklade historie zisti, ci sa ma v tabulke zavolat scrollTo
     * na zaklade toho, ci sa uzivatel vrazia z detailu nabidky alebo
     * prepina medzi jednotlivymi views
     * @returns boolean
     */
    const shouldScroll = (): boolean => {
        const url = navigationHistory[navigationHistory.length - 2]
        if (!url) return false;

        const params = url.split('/');
        const lastParam = params[params.length - 1]
        const isNumber = Number.parseInt(lastParam)
        return !Number.isNaN(isNumber)
    }

    const modifyCacheRecord = (id: any, modifyFn: (item: any) => any) => {
        const tableCache = getTableCache();
        let list = tableCache?.data?.list;

        if (Array.isArray(list)) {
            // eslint-disable-next-line
            const index = list.findIndex(item => item.id == id);
            if (index !== -1) {
                const newList = [
                    ...list.slice(0, index),
                    modifyFn(list[index]),
                    ...list.slice(index + 1)
                ];
                setTableCache({ ...tableCache, data: { ...tableCache.data, list: newList } });
            }
        }
    }

    const resetTableScroll = () => {
        let existingScroll = JSON.parse(localStorage.getItem("tableScroll") ?? "null") as LocalStorageTableCache;
        const updatedScroll = {
            ...(existingScroll || {}),
            [typ]: {x: 0, y: 0} as TableScroll
        };
        localStorage.setItem("tableScroll", JSON.stringify(updatedScroll));
    }

    return { getTableCache, setTableCache, clearTableCache, modifyCacheRecord, shouldScroll, resetTableScroll };
}